import { useState, useEffect } from 'react'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { useAppStore } from '../../stores/app'
import { useVisualConfig, useUpdateVisualConfig } from '../../hooks/useAdminConfig'
import { LoadingSpinner } from '../ui/LoadingSpinner'

const DEFAULT_PROVIDERS = [
  { value: 'gemini3', label: 'Gemini 3' },
  { value: 'imagen4', label: 'Imagen 4' },
] as const

const FALLBACK_PROVIDER_OPTIONS = ['gemini3', 'imagen4', 'nano_banana_2'] as const

const IMAGE_SIZES = ['0.5K', '1K', '2K', '4K'] as const

const THINKING_LEVELS = ['low', 'medium', 'high'] as const

const BEHAVIOR_OPTIONS = [
  { value: 'wait_and_retry', label: 'Wait and retry' },
  { value: 'fallback', label: 'Fallback' },
  { value: 'fail', label: 'Fail' },
] as const

interface VisualConfigState {
  default_provider: string
  fallback_chain: string[]
  quality_presets: {
    draft: { image_size: string; thinking_level: string; use_grounding: boolean }
    standard: { image_size: string; thinking_level: string; use_grounding: boolean }
    premium: { image_size: string; thinking_level: string; use_grounding: boolean }
  }
  thinking_level_global: string
  max_retries: number
  retry_delay_seconds: number
  on_rate_limit: string
  on_content_filter: string
  on_api_error: string
}

const defaultState: VisualConfigState = {
  default_provider: 'gemini3',
  fallback_chain: ['gemini3', 'imagen4', 'nano_banana_2'],
  quality_presets: {
    draft: { image_size: '1K', thinking_level: 'low', use_grounding: false },
    standard: { image_size: '2K', thinking_level: 'high', use_grounding: false },
    premium: { image_size: '4K', thinking_level: 'high', use_grounding: true },
  },
  thinking_level_global: 'high',
  max_retries: 3,
  retry_delay_seconds: 2,
  on_rate_limit: 'wait_and_retry',
  on_content_filter: 'fallback',
  on_api_error: 'fallback',
}

function parseVisualConfig(raw: Record<string, unknown>): VisualConfigState {
  const fallbackChain = raw.fallback_chain as string[] | undefined
  const presets = raw.quality_presets as Record<string, Record<string, unknown>> | undefined
  const behavior = raw.fallback_behavior as Record<string, unknown> | undefined
  return {
    default_provider: typeof raw.default_provider === 'string' ? raw.default_provider : defaultState.default_provider,
    fallback_chain: Array.isArray(fallbackChain) ? [...fallbackChain] : defaultState.fallback_chain,
    quality_presets: {
      draft: {
        image_size: (presets?.draft?.image_size as string) ?? defaultState.quality_presets.draft.image_size,
        thinking_level: (presets?.draft?.thinking_level as string) ?? defaultState.quality_presets.draft.thinking_level,
        use_grounding: Boolean(presets?.draft?.use_grounding ?? defaultState.quality_presets.draft.use_grounding),
      },
      standard: {
        image_size: (presets?.standard?.image_size as string) ?? defaultState.quality_presets.standard.image_size,
        thinking_level: (presets?.standard?.thinking_level as string) ?? defaultState.quality_presets.standard.thinking_level,
        use_grounding: Boolean(presets?.standard?.use_grounding ?? defaultState.quality_presets.standard.use_grounding),
      },
      premium: {
        image_size: (presets?.premium?.image_size as string) ?? defaultState.quality_presets.premium.image_size,
        thinking_level: (presets?.premium?.thinking_level as string) ?? defaultState.quality_presets.premium.thinking_level,
        use_grounding: Boolean(presets?.premium?.use_grounding ?? defaultState.quality_presets.premium.use_grounding),
      },
    },
    thinking_level_global: (raw.thinking_level_global as string) ?? (raw.thinking as Record<string, unknown>)?.default_level as string ?? defaultState.thinking_level_global,
    max_retries: Number(behavior?.max_retries ?? defaultState.max_retries) || defaultState.max_retries,
    retry_delay_seconds: Number(behavior?.retry_delay_seconds ?? defaultState.retry_delay_seconds) ?? defaultState.retry_delay_seconds,
    on_rate_limit: (behavior?.on_rate_limit as string) ?? defaultState.on_rate_limit,
    on_content_filter: (behavior?.on_content_filter as string) ?? defaultState.on_content_filter,
    on_api_error: (behavior?.on_api_error as string) ?? defaultState.on_api_error,
  }
}

function stateToPayload(s: VisualConfigState): Record<string, unknown> {
  return {
    default_provider: s.default_provider,
    fallback_chain: s.fallback_chain,
    quality_presets: s.quality_presets,
    thinking_level_global: s.thinking_level_global,
    fallback_behavior: {
      max_retries: s.max_retries,
      retry_delay_seconds: s.retry_delay_seconds,
      on_rate_limit: s.on_rate_limit,
      on_content_filter: s.on_content_filter,
      on_api_error: s.on_api_error,
    },
  }
}

export function SettingsVisualSection() {
  const organizationId = useAppStore((state) => state.organizationId)
  const { data: configRaw = {}, isLoading } = useVisualConfig(organizationId ?? undefined)
  const updateVisualConfig = useUpdateVisualConfig()

  const [state, setState] = useState<VisualConfigState>(defaultState)

  useEffect(() => {
    setState(parseVisualConfig(configRaw as Record<string, unknown>))
  }, [configRaw])

  const moveFallback = (index: number, dir: 1 | -1) => {
    const next = index + dir
    if (next < 0 || next >= state.fallback_chain.length) return
    const chain = [...state.fallback_chain]
    ;[chain[index], chain[next]] = [chain[next], chain[index]]
    setState((s) => ({ ...s, fallback_chain: chain }))
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organizationId) return
    await updateVisualConfig.mutateAsync({
      orgId: organizationId,
      data: stateToPayload(state),
    })
  }

  const busy = updateVisualConfig.isPending

  if (isLoading) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-800">Generacion de imagenes</h2>
        <p className="text-sm text-slate-500 mt-1">Modelos y parametros de generacion visual (Imagen, Nano Banana 2).</p>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </section>
    )
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-slate-800">Generacion de imagenes</h2>
      <p className="text-sm text-slate-500 mt-1">Modelos y parametros de generacion visual (Imagen, Nano Banana 2).</p>

      <form onSubmit={handleSave} className="mt-6 space-y-8">
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">Default Provider</h3>
          <select
            className="input max-w-xs"
            value={state.default_provider}
            onChange={(e) => setState((s) => ({ ...s, default_provider: e.target.value }))}
            disabled={busy}
          >
            {DEFAULT_PROVIDERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">Fallback Chain</h3>
          <p className="text-xs text-slate-500 mb-2">Orden de proveedores a usar si el principal falla (arriba primero).</p>
          <ul className="space-y-1">
            {state.fallback_chain.map((provider, index) => (
              <li key={`${provider}-${index}`} className="flex items-center gap-2">
                <span className="inline-block min-w-[8rem] px-3 py-1.5 rounded-md bg-slate-200 text-slate-800 text-sm font-mono">
                  {provider}
                </span>
                <button
                  type="button"
                  onClick={() => moveFallback(index, -1)}
                  disabled={busy || index === 0}
                  className="p-1 rounded hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveFallback(index, 1)}
                  disabled={busy || index === state.fallback_chain.length - 1}
                  className="p-1 rounded hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex gap-2 flex-wrap">
            {FALLBACK_PROVIDER_OPTIONS.filter((p) => !state.fallback_chain.includes(p)).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setState((s) => ({ ...s, fallback_chain: [...s.fallback_chain, p] }))}
                disabled={busy}
                className="btn btn-secondary text-sm py-1 px-2"
              >
                + {p}
              </button>
            ))}
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">Quality Presets</h3>
          {(['draft', 'standard', 'premium'] as const).map((presetKey) => {
            const preset = state.quality_presets[presetKey]
            return (
              <div key={presetKey} className="mb-6 last:mb-0">
                <h4 className="text-sm font-medium text-slate-600 capitalize mb-3">{presetKey}</h4>
                <div className="grid gap-4 sm:grid-cols-3 pl-4 border-l-2 border-slate-200">
                  <div>
                    <label className="label">Image size</label>
                    <select
                      className="input"
                      value={preset.image_size}
                      onChange={(e) =>
                        setState((s) => ({
                          ...s,
                          quality_presets: {
                            ...s.quality_presets,
                            [presetKey]: { ...s.quality_presets[presetKey], image_size: e.target.value },
                          },
                        }))
                      }
                      disabled={busy}
                    >
                      {IMAGE_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Thinking level</label>
                    <select
                      className="input"
                      value={preset.thinking_level}
                      onChange={(e) =>
                        setState((s) => ({
                          ...s,
                          quality_presets: {
                            ...s.quality_presets,
                            [presetKey]: { ...s.quality_presets[presetKey], thinking_level: e.target.value },
                          },
                        }))
                      }
                      disabled={busy}
                    >
                      {THINKING_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preset.use_grounding}
                        onChange={(e) =>
                          setState((s) => ({
                            ...s,
                            quality_presets: {
                              ...s.quality_presets,
                              [presetKey]: { ...s.quality_presets[presetKey], use_grounding: e.target.checked },
                            },
                          }))
                        }
                        disabled={busy}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-700">Use grounding</span>
                    </label>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">Thinking Level (global)</h3>
          <select
            className="input max-w-xs"
            value={state.thinking_level_global}
            onChange={(e) => setState((s) => ({ ...s, thinking_level_global: e.target.value }))}
            disabled={busy}
          >
            {THINKING_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">Fallback behavior</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Max Retries</label>
              <input
                type="number"
                className="input"
                min={0}
                max={10}
                value={state.max_retries}
                onChange={(e) => setState((s) => ({ ...s, max_retries: Number(e.target.value) }))}
                disabled={busy}
              />
            </div>
            <div>
              <label className="label">Retry Delay (s)</label>
              <input
                type="number"
                className="input"
                min={0}
                max={60}
                value={state.retry_delay_seconds}
                onChange={(e) => setState((s) => ({ ...s, retry_delay_seconds: Number(e.target.value) }))}
                disabled={busy}
              />
            </div>
            <div>
              <label className="label">On rate limit</label>
              <select
                className="input"
                value={state.on_rate_limit}
                onChange={(e) => setState((s) => ({ ...s, on_rate_limit: e.target.value }))}
                disabled={busy}
              >
                {BEHAVIOR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">On content filter</label>
              <select
                className="input"
                value={state.on_content_filter}
                onChange={(e) => setState((s) => ({ ...s, on_content_filter: e.target.value }))}
                disabled={busy}
              >
                {BEHAVIOR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">On API error</label>
              <select
                className="input"
                value={state.on_api_error}
                onChange={(e) => setState((s) => ({ ...s, on_api_error: e.target.value }))}
                disabled={busy}
              >
                {BEHAVIOR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200">
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </section>
  )
}
